import { Button, FormLabel, TextField } from '@automata/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import { FC, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  DataFieldFormValues,
  FIELD_VALIDATION_CONSTANTS,
  convertFormDataToFieldData,
  createDataFieldFormSchema,
  formatOptionsForDisplay,
  sanitizeId,
} from './utils';
import type { AdditionalDataInput } from '../../../../types';

interface DataFieldFormProps {
  onSubmit: (field: AdditionalDataInput) => void;
  onCancel: () => void;
  existingFieldIds: string[];
  initialData?: Partial<AdditionalDataInput>;
  currentFieldId?: string;
  submitButtonText?: string;
  handleOpenDeleteDialog?: () => void;
}

type FieldType = 'string' | 'integer' | 'float' | 'boolean';

export const DataFieldForm: FC<DataFieldFormProps> = ({
  onSubmit,
  onCancel,
  existingFieldIds,
  initialData,
  currentFieldId,
  submitButtonText = 'Add Field',
  handleOpenDeleteDialog,
}) => {
  const formSchema = useMemo(
    () => createDataFieldFormSchema(existingFieldIds, currentFieldId),
    [existingFieldIds, currentFieldId]
  );

  const {
    control,
    register,
    watch,
    setValue,
    trigger,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<DataFieldFormValues>({
    defaultValues: {
      label: initialData?.label || '',
      id: initialData?.id || '',
      type: initialData?.type || undefined,
      defaultValue: initialData?.default_value?.toString() || '',
      options: formatOptionsForDisplay(initialData?.options),
    },
    resolver: zodResolver(formSchema),
    mode: 'all',
  });

  const watchedType = watch('type');
  const watchedLabel = watch('label');

  const handleLabelBlur = async () => {
    await trigger('label');
    const currentId = watch('id');
    if (!currentId || currentId === sanitizeId(initialData?.label || '')) {
      const sanitized = sanitizeId(watchedLabel);
      setValue('id', sanitized);
      await trigger('id');
    }
  };

  const handleTypeChange = (newType: FieldType) => {
    setValue('type', newType);
    setValue('defaultValue', '');
    setValue('options', '');
  };

  const onSubmitForm = handleSubmit((data) => {
    const fieldData = convertFormDataToFieldData(data);
    if (!fieldData) return;

    onSubmit(fieldData);
    reset();
  });

  const handleCancel = () => {
    reset();
    onCancel();
  };

  const renderDefaultValueField = () => {
    if (watchedType === 'boolean') {
      return (
        <Controller
          name="defaultValue"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.defaultValue}>
              <FormLabel>Default Value</FormLabel>
              <RadioGroup {...field} row>
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="True"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="False"
                />
              </RadioGroup>
              {errors.defaultValue && (
                <FormHelperText error>
                  {errors.defaultValue.message}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      );
    }

    const inputType = watchedType === 'string' ? 'text' : 'number';
    const step = watchedType === 'float' ? '0.01' : '1';

    return (
      <TextField
        fullWidth
        label="Default Value"
        type={inputType}
        slotProps={{
          htmlInput: {
            step: watchedType !== 'string' ? step : undefined,
          },
        }}
        {...register('defaultValue')}
        placeholder="None"
        error={!!errors.defaultValue}
        helperText={errors.defaultValue?.message}
      />
    );
  };

  return (
    <form onSubmit={onSubmitForm}>
      <Stack gap={1} width="536px">
        <TextField
          fullWidth
          label="Label"
          placeholder="Enter value"
          required
          {...register('label')}
          onBlur={handleLabelBlur}
          error={!!errors.label}
          helperText={
            errors.label?.message ||
            `${watchedLabel.length}/${FIELD_VALIDATION_CONSTANTS.MAX_LABEL_LENGTH}`
          }
        />

        <TextField
          fullWidth
          label="ID Name"
          placeholder="Enter value"
          required
          {...register('id')}
          error={!!errors.id}
          helperText={errors.id?.message}
        />

        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="Type"
              select
              fullWidth
              required
              onChange={(e) => {
                const newType = e.target.value as FieldType;
                field.onChange(newType);
                handleTypeChange(newType);
              }}
            >
              <MenuItem value="" disabled>
                Select type
              </MenuItem>
              <MenuItem value="string">String</MenuItem>
              <MenuItem value="integer">Integer</MenuItem>
              <MenuItem value="float">Float</MenuItem>
              <MenuItem value="boolean">Boolean</MenuItem>
            </TextField>
          )}
        />

        <TextField
          fullWidth
          label="Options (optional)"
          placeholder="e.g., 70-minute, 100-minute"
          {...register('options')}
          error={!!errors.options}
          helperText={
            errors.options?.message ||
            'For dropdown display. Enter comma-separated values'
          }
        />

        {renderDefaultValueField()}

        <Stack
          flexDirection="row"
          gap={1}
          width="100%"
          justifyContent="flex-end"
          marginTop={2}
        >
          <Button variant="outlined" onClick={handleCancel} type="button">
            Cancel
          </Button>
          {handleOpenDeleteDialog && (
            <Button
              variant="contained"
              color="error"
              onClick={handleOpenDeleteDialog}
            >
              Delete Field
            </Button>
          )}
          <Button variant="contained" type="submit" disabled={!isValid}>
            {submitButtonText}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
